/**
 * What the save and switch flows share: the step/offer types, the copy, the
 * client's copy of the backend's backfill rule, and the helpers that are not
 * hooks. `useSaveRule.ts` re-exports the public ones.
 */
import { emit, type Props } from '../../platform/analytics/analytics'
import { toWrite, type ReadRule, type RuleDraft } from './ruleWire'
import { RulesError } from './useRules'

export type SaveStep =
  | { k: 'idle' }
  | { k: 'saving' }
  | { k: 'counting' }
  | {
      k: 'confirm'
      id: string
      count: number
      atLeast: boolean
      skippedLocked: number
      alreadySet: number
      from: string
      defaulted: boolean
    }
  | { k: 'applying' }
  | { k: 'failed'; message: string; saved: boolean }

export type ConfirmStep = Extract<SaveStep, { k: 'confirm' }>

/**
 * What the confirmation is about. Held apart from `step` because the dialog
 * stays up through `applying` and a failed apply, which carry no count.
 * `via` picks the button wording: the builder's Save, or the list's switch.
 */
export type Offer = Omit<ConfirmStep, 'k'> & {
  via: 'save' | 'enable'
  name: string
  categoryId: string | null
  /** What reaches the wire as `backfill_start_date`; null = the server's 90 days. */
  start: string | null
  /** Order in which offers were raised. Whichever is older keeps the screen. */
  raised: number
}

/** The batch the Undo toast offers to revert — always the SAVE response's id. */
export type UndoTarget = { batchId: string }

/** A save that failed with copy for the user. `saved` says whether the rule
 *  itself was written — the difference between "nothing changed" and "your
 *  rule is saved, but history is untouched". */
export class SaveError extends Error {
  readonly saved: boolean
  constructor(message: string, saved: boolean) {
    super(message)
    this.name = 'SaveError'
    this.saved = saved
  }
}

export const NOT_APPLIED =
  "Your rule is saved, but it wasn't applied to past transactions. Nothing in your history changed. Try again, or save without past transactions."

/** An error that is not a `RulesError` is a request that got no response: a dropped connection or the
 *  client-wide timeout (#507). The server may have committed it, so "nothing was changed" would be a claim
 *  nobody checked. A `RulesError` is a response, and a refused write changed nothing. */
export const mayHaveCommitted = (e: unknown) => !(e instanceof RulesError) && !(e instanceof SaveError)

export const NOT_CONFIRMED_SAVE =
  "We didn't hear back, so we can't tell whether this rule was saved. Check your rules list before trying again."
export const NOT_CONFIRMED_APPLY =
  "Your rule is saved, but we couldn't confirm whether it was applied to past transactions. Check your rules list and activity, then try again."

export function saveFailureCopy(e: unknown, name: string): string {
  if (e instanceof SaveError) return e.message
  if (mayHaveCommitted(e)) return NOT_CONFIRMED_SAVE
  return e instanceof RulesError && e.status === 409
    ? `You already have a rule called “${name}”. Find it in your rules list, or pick another name.`
    : "Couldn't save this rule. Nothing was changed."
}

/** The list switch's success toast; iOS keeps the pair in RuleCopy.enabledToast / disabledToast. */
export const ruleSwitchedToast = (name: string, on: boolean) => `“${name}” rule is ${on ? 'on' : 'off'}`

export const toggleFailed = (name: string, e?: unknown) =>
  e !== undefined && mayHaveCommitted(e)
    ? `We didn't hear back about “${name}”, so we can't tell whether it changed. Check the list.`
    : `Couldn't update “${name}”. Nothing was changed.`

/** The builder closed after step 1 wrote the rule (with `apply_to_past: false`)
 *  and before the flow finished. The write stands; this says so. */
export function withoutPastCopy(name: string, created: boolean): string {
  return created
    ? `“${name}” was created without applying it to past transactions.`
    : `Your changes to “${name}” were saved without applying them to past transactions.`
}

export const IDLE: SaveStep = { k: 'idle' }

export const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Whether saving `draft` over `base` makes the server backfill — the client's
 * copy of the backend's `save_triggers_backfill`, plus its `_backfill` refusal
 * of a disabled rule. `base` null is a create.
 *
 * Conditions and actions are compared as `toWrite` produces them, both sides
 * through the same function, so an untouched rule compares equal however the
 * server stored it.
 */
export function needsConfirm(draft: RuleDraft, base: ReadRule | null): boolean {
  if (!draft.enabled || !draft.applyToPast) return false
  if (base === null) return true
  const r = base.rule
  if (!r.apply_to_past || !r.is_enabled) return true
  // Unreachable from the builder (a read-only rule has no Edit); if it ever
  // is, asking is the safe answer.
  if (!base.editable) return true
  const now = toWrite(draft)
  const was = toWrite(base.draft)
  return !same(now.conditions, was.conditions) || !same(now.actions, was.actions)
}

export function categoryOf(read: ReadRule): string | null {
  if (!read.editable) return null
  return read.draft.actions.find((a) => a.type === 'set_category')?.value ?? null
}

/** Analytics never decides the outcome of a write that already happened. In
 *  development `emit` throws on a key that fails the vocabulary guard; that is
 *  a bug to see, not a reason to tell the user a saved rule was not saved. */
export function track(name: Parameters<typeof emit>[0], props?: Props) {
  try {
    emit(name, props)
  } catch (e) {
    console.error(e)
  }
}
